import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

// chakra ui
import { Flex, Button, IconButton, Image, Box, Text } from "@chakra-ui/react";
import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";

// react spring
import { useSprings, useSpring, animated } from "@react-spring/web";

// components
import MyButton from "../button/MyButton";
// logo controluce
import logoR from "../../assets/images/controluceLogoRWhite.png";
import logoL from "../../assets/images/controluceLogoLWhite.png";

// style
import style from "./navbar.module.css";

const MyNavbar = ({ isVisible }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const location = useLocation();
  const [isNavbarVisible, setNavbarVisible] = useState(true); // Tracks navbar visibility
  const [lastScrollY, setLastScrollY] = useState(0);

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setNavbarVisible(false);
      } else {
        setNavbarVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);

  const isTablet = windowWidth <= 850;
  // Map paths to page names
  const pageTitles = {
    "/": "Officina Illuminotecnica",
    "/gallery/event": "Galleria",
    "/material": "Materiali",
    "/contact": "Contatti",
    "/info": "Info",
  };

  // Get the current page title
  let page = pageTitles[location.pathname] || "Page Not Found";

  const queryParams = new URLSearchParams(location.search);
  const eventoParam = queryParams.get("event");

  if (location.pathname === "/lightbox") {
    if (eventoParam.indexOf(" ") !== -1) {
      let newArr = eventoParam.split(" ");

      let words = newArr.map((i) => i.charAt(0).toUpperCase() + i.slice(1));

      page = words.join(" ");
    } else {
      page = eventoParam.charAt(0).toUpperCase() + eventoParam.slice(1);
    }
    page = "Evento " + page;
  }

  // Creates animations for navigation links
  const [props] = useSprings(4, (i) => ({
    from: { y: -2000 },
    to: i,
    delay: i * 500,
  }));
  // animation for mobile menu
  const mobileMenuSpring = useSpring({
    from: { y: -1000 },
    to: { y: 0 },
    delay: 3000,
  });
  // Creates animation for the left logo
  const logoLeftSpring = useSpring({
    from: { x: -1000 },
    to: { x: 0 },
    delay: isTablet ? 500 : 2000,
  });

  // Creates animation for the right logo
  const logoRightSpring = useSpring({
    from: { y: -1000 },
    to: { y: 0 },
    delay: 200,
  });

  const navLink = [
    { text: "Home", to: "/", ariaLabel: "Link to Home" },
    { text: "Galleria", to: "/gallery/event", ariaLabel: "Link to Gallery" },
    { text: "Materiale", to: "/material", ariaLabel: "Link to Materials" },
    { text: "Contatti", to: "/contact", ariaLabel: "Link to Contact" },
  ];

  const menuLink = navLink.map((link, index) => (
    <Box key={index}>
      <MyButton
        text={link.text}
        to={link.to}
        ariaLabel={link.ariaLabel}
        // m={"1rem 0"}
        p={"0.5em"}
        variant={"ghost"}
        color={"#fff"}
        border={"none"}
        _hover={{ background: "none", color: "ambra" }}
      />
    </Box>
  ));

  return !isTablet ? (
    <Flex
      as={"nav"}
      className={isNavbarVisible ? style.visible : style.hidden}
      align="center"
      justify={"space-between"}
      p={{ md: "2rem 4rem", xl: "3rem" }}
      position={"sticky"}
      top={0}
      zIndex={10}
      background={"rgba(0, 0, 0, 0.8)"}
      box-shadow={"0 4px 30px rgba(0, 0, 0, 0.8)"}
      backdrop-filter={"blur(7px)"}
    >
      {/* Desktop */}

      <Box w={"20%"}>
        <Link to={"/"}>
          <Flex>
            <animated.div style={{ ...logoLeftSpring }}>
              <Image alt="Controluce Logo" src={logoL} />
            </animated.div>
            <animated.div style={{ ...logoRightSpring }}>
              <Image alt="Controluce Logo" src={logoR} />
            </animated.div>
          </Flex>
        </Link>
      </Box>

      <Text
        as="h1"
        m={0}
        fontWeight={800}
        opacity={1}
        transition={"opacity .3s"}
        fontSize={{ md: "1.2em", xl: "2rem" }}
        letterSpacing={"0.08em"}
      >
        {page}
      </Text>

      <Flex>
        {props.map(({ y }, i) => (
          <animated.div key={i} style={{ y }}>
            {menuLink[i]}
          </animated.div>
        ))}
      </Flex>
    </Flex>
  ) : (
    <Box
      background={"rgba(0, 0, 0, 0.8)"}
      box-shadow={"0 4px 30px rgba(0, 0, 0, 0.8)"}
      backdrop-filter={"blur(7px)"}
      as={"nav"}
      className={style.navbar}
      p={"0.6em"}
    >
      <Box w={"75%"}>
        <Link to={"/"}>
          <Flex>
            <animated.div style={{ ...logoLeftSpring }}>
              <Image alt="Controluce Logo" src={logoL} />
            </animated.div>
            <animated.div style={{ ...logoRightSpring }}>
              <Image alt="Controluce Logo" src={logoR} />
            </animated.div>
          </Flex>
        </Link>
      </Box>
      {/* Mobile */}

      <Flex mt={{ base: "1em" }} align={"center"} justify={"space-between"}>
        <Text
          as="h1"
          m={"0 0 0 0.2em"}
          fontWeight={800}
          opacity={1}
          transition={"opacity .3s"}
          fontSize={"1.08rem"}
          letterSpacing={"0.08em"}
        >
          {page}
        </Text>

        <IconButton
          style={{ background: "none" }}
          aria-label="Open Menu"
          icon={<HamburgerIcon boxSize={6} color={"#000"} />}
          onClick={() => setShowMobileMenu(true)}
          opacity={showMobileMenu ? 0 : 1}
          mr={2}
        />

        <Flex
          w="100vw"
          bgColor="rgba(0,0,0,0.9)"
          zIndex={20}
          pos="fixed"
          top="0"
          left="0"
          flexDir="column"
          pb={"2em"}
          transform={showMobileMenu ? "translateY(0)" : "translateY(-100%)"}
          transition={"transform 0.3s ease-in-out"}
          opacity={showMobileMenu ? 1 : 0}
        >
          <Flex justify="flex-end">
            <IconButton
              m={5}
              aria-label="Open Menu"
              variant={"ghost"}
              background={"none"}
              _hover={{
                background: "none",
              }}
              icon={
                <CloseIcon
                  color={"#fff"}
                  boxSize={4}
                  _active={{ color: "ambra" }}
                />
              }
              onClick={() => setShowMobileMenu(false)}
            />
          </Flex>

          <Flex flexDir="column" align="center">
            <Link to={"/"} onClick={() => setShowMobileMenu(false)}>
              <Button
                variant="ghost"
                color={"#f4f4f4"}
                aria-label="Home"
                my={5}
                w="100%"
                _hover={{
                  background: "none",
                  color: "ambra",
                }}
              >
                Home
              </Button>
            </Link>

            <Link
              to={"/gallery/event"}
              onClick={() => setShowMobileMenu(false)}
            >
              <Button
                variant="ghost"
                color={"#f4f4f4"}
                aria-label="Material"
                my={5}
                w="100%"
                _hover={{
                  background: "none",
                  color: "ambra",
                }}
              >
                Galleria
              </Button>
            </Link>

            <Link to={"/material"} onClick={() => setShowMobileMenu(false)}>
              <Button
                variant="ghost"
                color={"#f4f4f4"}
                aria-label="Material"
                my={5}
                w="100%"
                _hover={{
                  background: "none",
                  color: "ambra",
                }}
              >
                Materiali
              </Button>
            </Link>

            <Link to={"/contact"} onClick={() => setShowMobileMenu(false)}>
              <Button
                variant="ghost"
                color={"#f4f4f4"}
                aria-label="Contact"
                my={5}
                w="100%"
                _hover={{
                  background: "none",
                  color: "ambra",
                }}
              >
                Contatti
              </Button>
            </Link>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export default MyNavbar;
